import { useState, useContext, useEffect, useRef } from 'react';
import GlobalContext from "../context/GlobalContext.js";
import HeaderBackground from '../components/headerbackground.js';
import getAPI from '../functions/API/getAPI.js';
import moment from 'moment';
import { jsPDF } from "jspdf";
import autoTable from 'jspdf-autotable';
import { toast } from '../lib/toast.js';
import postAPI from '../functions/API/postAPI.js';
import AccountSwitcher from '../components/AccountSwitcher.js';
import { useLocation, useNavigate } from 'react-router-dom';
import { CreditCard, ArrowUpCircle, Calendar, Download, ChevronRight, X } from 'lucide-react';

export default function Transactions() {

    const [agreement, setAgreement] = useState();
    const [account, setAccount] = useState('');
    const [accounts, setAccounts] = useState();
    const [profile, setProfile] = useState();
    const [loading, isLoading] = useState(true);
    const [transactions, setTransactions] = useState();
    const [startDate, setStartDate] = useState(moment().subtract(1, 'month').format('YYYY-MM-DD'));
    const [endDate, setEndDate] = useState(moment().format("YYYY-MM-DD"));
    const [loadingspinner, setLoadingSpinner] = useState(false);
    const { theme, themeCSS, settings } = useContext(GlobalContext);
    const [formattedAddress, setFormattedAddress] = useState([]);
    const [accountSelectModal, setAccountSelectModal] = useState(false);
    const [selectedTransaction, setSelectedTransaction] = useState(null);

    const location = useLocation();
    const navigate = useNavigate();
    const pdfRef = useRef(null);
    const startRef = useRef(null);
    const endRef = useRef(null);
    const primaryColor = settings?.primary_hex_colour || '#813468';

    // ── PDF download — untouched ──────────────────────────────────────────────
    function handleDownload() {
        const doc = new jsPDF('p', 'pt', 'a4');
        autoTable(doc, { html: '#transactiontable', startY: 250, theme: 'grid', headStyles: { fillColor: settings.primary_hex_colour } });
        const content = pdfRef.current;
        doc.html(content, {
            callback: function (doc) {
                doc.save('Account Transactions.pdf');
            }, x: 10, y: 10,
            html2canvas: { scale: 0.90 },
        });
    }

    // ── Data fetching ─────────────────────────────────────────────────────────
    async function GetAPIData() {
        const agreementreference = localStorage.getItem('agreement');
        const AgreementResponse = await getAPI('agreements/' + agreementreference);
        if (AgreementResponse.response.ok) {
            setAccounts(AgreementResponse.data.accounts);
            const ProfileDetailsResponse = await getAPI('userdetails/summary');
            var address = AgreementResponse.data.address;
            var addressArr = address.split(',');
            let finaladdressarray = addressArr.map(function (addresselement) {
                return addresselement.trim();
            });
            setFormattedAddress(finaladdressarray);
            setProfile(ProfileDetailsResponse.data);
            setAgreement(AgreementResponse.data);

            const passedAccountId = location.state?.accountId;
            const matchedAccount = passedAccountId
                ? AgreementResponse.data.accounts.find(a => a.accountid === passedAccountId)
                : null;

            const targetAccount = matchedAccount || (AgreementResponse.data.accounts.length === 1 ? AgreementResponse.data.accounts[0] : null);

            if (targetAccount) {
                setAccount(targetAccount);
                let TransactionURL = 'transactions/' + targetAccount.accountid + "?startdate=" + startDate + "&enddate=" + endDate;
                const TransactionResponse = await getAPI(TransactionURL);
                setTransactions(TransactionResponse.data);
            } else {
                setAccountSelectModal(true);
            }
        }
        isLoading(false);
    }

    async function setSelectedAccount(acct) {
        setAccountSelectModal(false);
        isLoading(true);
        setAccount(acct);
        let TransactionURL = 'transactions/' + acct.accountid + "?startdate=" + startDate + "&enddate=" + endDate;
        const AgreementDetail = await getAPI('agreements/' + localStorage.getItem('agreement'));
        const ProfileDetailsResponse = await getAPI('userdetails/summary');
        const TransactionResponse = await getAPI(TransactionURL);
        var address = AgreementDetail.data.address;
        var addressArr = address.split(',');
        let finaladdressarray = addressArr.map(function (addresselement) {
            return addresselement.trim();
        });
        setFormattedAddress(finaladdressarray);
        setTransactions(TransactionResponse.data);
        setProfile(ProfileDetailsResponse.data);
        setAgreement(AgreementDetail.data);
        isLoading(false);
    }

    async function applyFilter() {
        setLoadingSpinner(true);
        let TransactionURL = 'transactions/' + account.accountid + "?startdate=" + startDate + "&enddate=" + endDate;
        const TransactionResponse = await getAPI(TransactionURL);
        setTransactions(TransactionResponse.data);
        setLoadingSpinner(false);
    }

    // ── AllPay payment — untouched ────────────────────────────────────────────
    async function makeAllPayPayment() {
        const body = { reference: agreement.reference };
        let PaymentProviderDetail = await postAPI('makepayment', body);
        let response = PaymentProviderDetail.response;
        if (response.ok) {
            window.location.href = '/allpaypayment/' + account.accountreference + "/" + PaymentProviderDetail.data.Token + "/" + encodeURIComponent(settings.allpay_gatewayurl) + "/" + PaymentProviderDetail.data.ClientCode + "/web";
        } else {
            toast.error('There was an error connecting to the payment service. Please try again later');
        }
    }

    useEffect(() => {
        GetAPIData();
    }, []);

    // ── Group transactions by date ────────────────────────────────────────────
    function groupByDate(txns) {
        const groups = [];
        const seen = {};
        txns.forEach(t => {
            const key = moment(t.date).format('D MMMM YYYY').toUpperCase();
            if (!seen[key]) {
                seen[key] = [];
                groups.push({ label: key, items: seen[key] });
            }
            seen[key].push(t);
        });
        return groups;
    }

    function isCredit(transaction) {
        return transaction.type !== 'charge';
    }

    function SkeletonCard() {
        return (
            <div className={`${themeCSS.cardBg} rounded-2xl shadow-sm p-4 flex items-center gap-3`}>
                <div className={`w-10 h-10 rounded-xl flex-shrink-0 ${themeCSS.skeleton} animate-pulse`} />
                <div className="flex-1 space-y-2">
                    <div className={`h-4 w-2/3 rounded-full ${themeCSS.skeleton} animate-pulse`} />
                    <div className={`h-3 w-1/3 rounded-full ${themeCSS.skeleton} animate-pulse`} />
                </div>
                <div className={`h-4 w-16 rounded-full ${themeCSS.skeleton} animate-pulse`} />
            </div>
        );
    }

    const grouped = transactions ? groupByDate(transactions) : [];

    function TransactionDetailModal() {
        const t = selectedTransaction;
        if (!t) return null;
        const credit = isCredit(t);
        const formattedDate = moment(t.date).format('dddd, Do MMMM YYYY');
        const typeLabel = credit ? 'Payment received' : 'Charge applied';

        const modalContent = (
            <>
                {/* Date */}
                <div className="text-center py-3">
                    <p className={`text-xs font-medium uppercase tracking-wider ${themeCSS.cardMuted}`}>Date</p>
                    <p className={`text-sm font-bold mt-0.5 ${themeCSS.cardTitle}`}>{formattedDate}</p>
                </div>

                <div className={`border-t ${theme === 'dark' ? 'border-slate-600' : 'border-gray-200'}`} />

                {/* Amount card */}
                <div className={`mt-4 rounded-xl ${themeCSS.cardBg} shadow-sm p-6 flex flex-col items-center`}>
                    <div className={`w-14 h-14 rounded-full flex items-center justify-center mb-4 ${credit ? 'bg-green-100' : 'bg-red-100'}`}>
                        {credit
                            ? <CreditCard size={24} className="text-green-600" />
                            : <ArrowUpCircle size={24} className="text-red-500" />
                        }
                    </div>
                    <p className={`text-2xl font-bold ${credit ? 'text-green-600' : 'text-red-500'}`}>
                        {credit ? '-' : ''}£{t.value}
                    </p>
                    <p className={`text-sm mt-1 ${themeCSS.cardMuted}`}>{typeLabel}</p>
                </div>

                {/* Description */}
                <div className={`mt-3 rounded-xl ${themeCSS.cardBg} shadow-sm px-5 py-4`}>
                    <p className={`text-xs font-medium uppercase tracking-wider ${themeCSS.cardMuted}`}>Description</p>
                    <p className={`text-sm font-semibold mt-1 ${themeCSS.cardTitle}`}>{t.description}</p>
                </div>

                {/* Running balance */}
                {t.runningbalance !== undefined && (
                    <div className={`mt-3 rounded-xl ${themeCSS.cardBg} shadow-sm px-5 py-4`}>
                        <p className={`text-xs font-medium uppercase tracking-wider ${themeCSS.cardMuted}`}>Running Balance</p>
                        <p className={`text-sm font-semibold mt-1 ${themeCSS.cardTitle}`}>
                            £{t.runningbalance} {t.runningbalance == 0 || String(t.runningbalance).includes('-') ? 'Credit' : 'Arrears'}
                        </p>
                    </div>
                )}
            </>
        );

        return (
            <>
                {/* Backdrop */}
                <div
                    className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm animate-fadeIn"
                    onClick={() => setSelectedTransaction(null)}
                />

                {/* Mobile: bottom sheet */}
                <div className={`fixed inset-x-0 bottom-0 z-50 animate-slideUp rounded-t-2xl shadow-2xl max-h-[85vh] overflow-y-auto lg:hidden ${
                    theme === 'dark' ? 'bg-slate-800' : theme === 'highContrast' ? 'bg-white' : 'bg-gray-50'
                }`}>
                    <div className="flex justify-center pt-3 pb-1">
                        <div className={`w-10 h-1 rounded-full ${theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'}`} />
                    </div>
                    <div className="flex items-center justify-between px-5 pb-2">
                        <h2 className={`text-lg font-bold ${themeCSS.cardTitle}`}>Transaction Details</h2>
                        <button onClick={() => setSelectedTransaction(null)} className="text-sm font-semibold" style={{ color: primaryColor }}>Done</button>
                    </div>
                    <div className="px-5 pb-8">
                        {modalContent}
                    </div>
                </div>

                {/* Desktop: centered modal */}
                <div className="hidden lg:flex fixed inset-0 z-50 items-center justify-center p-4">
                    <div className={`w-full max-w-lg rounded-2xl shadow-xl overflow-hidden animate-slideUp ${
                        theme === 'dark' ? 'bg-slate-800' : theme === 'highContrast' ? 'bg-white' : 'bg-gray-50'
                    }`}>
                        <div className="flex items-center justify-between px-5 pt-5 pb-3">
                            <h2 className={`text-lg font-bold ${themeCSS.cardTitle}`}>Transaction Details</h2>
                            <button
                                onClick={() => setSelectedTransaction(null)}
                                className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${theme === 'dark' ? 'hover:bg-slate-700' : 'hover:bg-gray-200'}`}
                            >
                                <X size={18} className={themeCSS.cardMuted} strokeWidth={2} />
                            </button>
                        </div>
                        <div className="px-5 pb-6 max-h-[70vh] overflow-y-auto">
                            {modalContent}
                        </div>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            {selectedTransaction && <TransactionDetailModal />}
            <div className="min-h-screen" style={{ backgroundColor: themeCSS.pageBg }}>
                <HeaderBackground
                    pageTitle="Transactions"
                    pageSubtitle={account?.type ? `${account.type}${account.accountreference ? ` · #${account.accountreference}` : ''}` : undefined}
                    onBack={() => navigate(-1)}
                />

                <div className="max-w-2xl mx-auto px-4 py-4 pb-32">

                    {/* Date filter */}
                    <div className={`${themeCSS.cardBg} rounded-2xl shadow-sm p-4 mb-4`}>
                        <div className="grid grid-cols-2 gap-3 mb-3">
                            {[
                                { label: 'Start date', value: startDate, onChange: e => setStartDate(e.target.value), ref: startRef },
                                { label: 'End date', value: endDate, onChange: e => setEndDate(e.target.value), ref: endRef },
                            ].map(({ label, value, onChange, ref }) => (
                                <div
                                    key={label}
                                    onClick={() => ref.current?.showPicker?.()}
                                    className={`relative border ${themeCSS.cardBorder} rounded-xl px-3 py-2.5 cursor-pointer`}
                                >
                                    <p className={`text-xs ${themeCSS.cardMuted} mb-0.5`}>{label}</p>
                                    <div className="flex items-center justify-between gap-2">
                                        <p className={`text-sm font-semibold ${themeCSS.cardTitle}`}>
                                            {moment(value).format('ddd, MMM D')}
                                        </p>
                                        <Calendar size={15} className={themeCSS.cardMuted} />
                                    </div>
                                    <input
                                        ref={ref}
                                        type="date"
                                        value={value}
                                        onChange={onChange}
                                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                    />
                                </div>
                            ))}
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={applyFilter}
                                disabled={loadingspinner}
                                className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white transition-opacity disabled:opacity-60"
                                style={{ backgroundColor: primaryColor }}
                            >
                                {loadingspinner ? 'Loading…' : 'Apply'}
                            </button>
                            <button
                                onClick={handleDownload}
                                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold border ${themeCSS.cardBorder} ${themeCSS.cardText} ${themeCSS.cardHover} transition-colors`}
                            >
                                <Download size={15} />
                                Download
                            </button>
                        </div>
                    </div>

                    {/* Transaction list */}
                    {loading ? (
                        <div className="space-y-3">
                            <SkeletonCard />
                            <SkeletonCard />
                            <SkeletonCard />
                            <SkeletonCard />
                        </div>
                    ) : !transactions || transactions.length === 0 ? (
                        <div className={`border-2 border-dashed ${themeCSS.cardBorder} rounded-2xl p-8 text-center`}>
                            <CreditCard size={32} className={`mx-auto mb-3 ${themeCSS.cardMuted}`} />
                            <p className={`text-sm font-semibold ${themeCSS.cardTitle}`}>No transactions</p>
                            <p className={`text-xs mt-1 ${themeCSS.cardMuted}`}>No transactions found for the selected date range.</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {grouped.map(group => (
                                <div key={group.label}>
                                    <p className={`text-xs font-bold tracking-wider mb-2 px-1 ${themeCSS.cardMuted}`}>
                                        {group.label}
                                    </p>
                                    <div className="space-y-2">
                                        {group.items.map((transaction, i) => {
                                            const credit = isCredit(transaction);
                                            return (
                                                <div
                                                    key={i}
                                                    onClick={() => setSelectedTransaction(transaction)}
                                                    className={`${themeCSS.cardBg} rounded-2xl shadow-sm p-4 flex items-center gap-3 cursor-pointer ${themeCSS.cardHover} transition-colors`}
                                                >
                                                    <div className={`w-10 h-10 rounded-xl flex-shrink-0 flex items-center justify-center ${credit ? 'bg-green-100' : 'bg-red-100'}`}>
                                                        {credit
                                                            ? <CreditCard size={18} className="text-green-600" />
                                                            : <ArrowUpCircle size={18} className="text-red-500" />
                                                        }
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className={`text-sm font-semibold ${themeCSS.cardTitle} truncate`}>
                                                            {transaction.description}
                                                        </p>
                                                        <p className={`text-xs mt-0.5 ${themeCSS.cardMuted} capitalize`}>
                                                            {transaction.type}
                                                        </p>
                                                    </div>
                                                    <p className={`text-sm font-bold flex-shrink-0 ${credit ? 'text-green-600' : themeCSS.cardTitle}`}>
                                                        {credit ? '-' : ''}£{transaction.value}
                                                    </p>
                                                    <ChevronRight size={16} className={`flex-shrink-0 ${themeCSS.cardMuted}`} strokeWidth={1.5} />
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

            </div>

            {/* ── PDF Export (hidden) — untouched ──────────────────────────── */}
            <div style={{ display: 'none' }}>
                <div ref={pdfRef} id="downloadheader" className="bg-white w-[640px] p-2">
                    <div className="grid grid-cols-1 pb-2 text-right text-xl">
                        <h1 className="pb-2 text-2xl font-semibold text-right text-black">{settings.company_name}</h1>
                    </div>
                    {agreement && (
                        <div className="grid grid-cols-1 text-left px-4">
                            <div className="pb-4">
                                <div className='text-xs font-semibold pb-4 text-black'>Customer Reference: {agreement.reference}</div>
                                <p className='text-xs text-black'>{profile?.firstname} {profile?.lastname}</p>
                                {formattedAddress && (
                                    formattedAddress.map(function (line, index) {
                                        return (
                                            <p key={index} className='text-xs text-black'>{line}</p>
                                        );
                                    })
                                )}
                                <p className='pb-2 text-xs text-black'>{moment().format('Do MMMM YYYY')}</p>
                                <p className='pb-2 text-xs text-black'>Your current balance is £{account.balance}. Please note that if you are in receipt of housing benefit, this balance may include expected housing benefit - the transactions below do not.</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <table style={{ display: 'none' }} id="transactiontable" className="table-auto overflow-auto w-full">
                <thead>
                    <tr className="text-[10px]">
                        <th className="pl-2 pb-2 text-left">Date</th>
                        <th className="pl-2 pb-2 text-left">Description</th>
                        <th className="pl-2 pb-2 text-left">Debit</th>
                        <th className="pl-2 pb-2 text-left">Credit</th>
                        <th className="pl-2 pb-2 text-left">Balance</th>
                    </tr>
                </thead>
                <tbody className="text-gray-600 text-sm font-light">
                    {transactions && (
                        transactions.map(function (transaction, index) {
                            return (
                                <tr key={index} className="border-b border-gray-200">
                                    <td className="pb-2 pl-2 text-left whitespace-wrap">
                                        <div className="flex items-center text-[10px]">
                                            <span>{moment(transaction.date).format("DD/MM/YYYY")}</span>
                                        </div>
                                    </td>
                                    <td className="pb-2 pl-2 text-left whitespace-wrap w-auto">
                                        <div className="flex items-center">
                                            <span className="text-[10px]">{transaction.description}</span>
                                        </div>
                                    </td>
                                    {transaction.type === "charge" ? (
                                        <>
                                            <td className="pb-2 pl-2 text-left whitespace-wrap">
                                                <span className="text-[10px]">£{transaction.value}</span>
                                            </td>
                                            <td className="pb-2 pl-2 text-left whitespace-wrap">
                                                <span className="text-[10px]"></span>
                                            </td>
                                        </>
                                    ) : (
                                        <>
                                            <td className="pb-2 pl-2 text-left whitespace-wrap">
                                                <span className="text-[10px]"></span>
                                            </td>
                                            <td className="pb-2 pl-2 text-left whitespace-wrap">
                                                <span className="text-[10px]">£{transaction.value}</span>
                                            </td>
                                        </>
                                    )}
                                    {transaction.runningbalance == 0 ? (
                                        <td className="pb-2 pl-2 text-left whitespace-wrap">
                                            <span className="text-[10px]">£{transaction.runningbalance} Credit</span>
                                        </td>
                                    ) : (
                                        transaction.runningbalance.includes("-", 0) ? (
                                            <td className="pb-2 pl-2 text-left whitespace-wrap">
                                                <span className="text-[10px]">£{transaction.runningbalance} Credit</span>
                                            </td>
                                        ) : (
                                            <td className="pb-2 pl-2 text-left whitespace-wrap">
                                                <span className="text-[10px]">£{transaction.runningbalance} Arrears</span>
                                            </td>
                                        )
                                    )}
                                </tr>
                            );
                        })
                    )}
                </tbody>
            </table>
            {/* END OF PDF Export Component */}

            {accounts && (
                <AccountSwitcher modalState={accountSelectModal} setModalState={setAccountSelectModal} titleText={'Please select an account'}>
                    {agreement && agreement.accounts.map((acct) => (
                        <div
                            onClick={() => { setSelectedAccount(acct); }}
                            key={acct.id}
                            className='flex cursor-default select-none rounded-xl p-3 hover:bg-gray-100'
                        >
                            <div className="flex-auto">
                                <p className='text-sm font-medium text-gray-900'>
                                    {acct.type} <span>(Account Reference: {acct.accountreference})</span>
                                </p>
                                <p className='text-sm text-gray-700'>
                                    {agreement.address}
                                </p>
                            </div>
                            {acct.direction === 'CR' ? (
                                <div className='flex h-10 w-10 flex-none items-center justify-center rounded-lg bg-green-600 w-28'>
                                    <p className='text-white text-lg'>£{acct.balance}</p>
                                </div>
                            ) : (
                                <div className='flex h-10 w-10 flex-none items-center justify-center rounded-lg bg-red-400 w-28'>
                                    <p className='text-white text-lg'>£{acct.balance}</p>
                                </div>
                            )}
                        </div>
                    ))}
                </AccountSwitcher>
            )}
        </>
    );
}
